import { ControlProps, JsonSchema, OwnPropsOfEnum } from '@jsonforms/core';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { InputNumber } from '@dynamika-shared/ui/InputNumber/InputNumber';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { TextArea } from '@dynamika-shared/ui/TextArea/TextArea';
import { FC, useMemo } from 'react';
import { MaskedInputNumber } from '@dynamika-shared/ui/InputNumber/MaskedInputNumber';
import type { DefaultOptionType } from 'antd/es/select';
import { twMerge } from '@utils/twMerge';

type InputControlProps = Omit<ControlProps, 'input'> & {
  input:
    | typeof Select
    | typeof InputNumber
    | typeof Input
    | typeof TextArea
    | typeof MaskedInputNumber;
  classNameInputWrapper?: string;
  schema: JsonSchema;
};

const InputControl: FC<InputControlProps> = ({
  id,
  label,
  required,
  input,
  path,
  handleChange,
  classNameInputWrapper,
  schema,
  errors,
  uischema,
  enabled,
}) => {
  const Component = input;

  const schemaWithDefaultPattern = {
    ...schema,
    pattern: schema?.pattern ?? '',
  };

  const handleComponentChange = (
    ev:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | string
      | number
      | null,
    option?: DefaultOptionType | DefaultOptionType[] | string | number | null
  ) => {
    if (typeof ev === 'string' || typeof ev === 'number') {
      return handleChange(path, ev);
    }
    if (ev?.target) {
      return handleChange(path, ev.target.value);
    }
    if (option !== undefined) {
      if (Array.isArray(option)) {
        return handleChange(
          path,
          option.map(opt => opt.value)
        );
      }
      return handleChange(
        path,
        typeof option === 'object' && option !== null && 'value' in option
          ? option.value
          : option
      );
    }
  };
  return (
    <div
      className={twMerge(
        'flex w-full justify-between border-b border-brightGray py-4 pl-1.5',
        !label && 'pl-0 pt-1 border-b-0'
      )}
    >
      {label && (
        <p className='min-w-[48%] pr-4 pt-2 text-1424 font-semibold'>
          {label}
          <span className='text-redOrange'>{required && ' *'}</span>
        </p>
      )}
      <Component
        className='min-w-[200px]'
        placeholder={label}
        schema={schemaWithDefaultPattern}
        id={id + '-input'}
        maxLength={schema?.maximum}
        data-testid={label}
        disabled={!enabled}
        onChange={handleComponentChange}
        classNameInputWrapper={classNameInputWrapper}
        classNameError={'block'}
        errors={errors}
        suffix={uischema?.options?.suffix}
      />
    </div>
  );
};

export const OneOfEnumControl = (props: ControlProps & OwnPropsOfEnum) => (
  <InputControl
    {...props}
    input={Select}
    classNameInputWrapper='max-w-[100%]'
  />
);

export const NumberControl = (props: ControlProps) => (
  <InputControl
    {...props}
    input={InputNumber}
    classNameInputWrapper='max-w-[100%]'
  />
);
export const TextControl = (props: ControlProps) => {
  const hasMask = !!props.schema?.pattern;

  const InputComponent = useMemo(() => {
    if (hasMask) {
      return MaskedInputNumber;
    }
    if (props.uischema.options?.multi) {
      return TextArea;
    }
    return Input;
  }, [hasMask, props.uischema.options?.multi]);
  return (
    <InputControl
      {...props}
      input={InputComponent}
      classNameInputWrapper='max-w-[100%]'
    />
  );
};
